import {
  Timestamp,
  collection,
  count,
  doc,
  getAggregateFromServer,
  onSnapshot,
  query,
  sum,
  where,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../firebase";
import { callCloudRunFunction } from "./cloudRunServices";
import {
  createFlyerInstanceId,
  normalizeFlyerInstanceId,
} from "./flyerInstanceIds";
import {
  QR_FLYER_CAMPAIGN,
  QR_FLYER_MEDIUM,
  QR_FLYER_VERSION,
} from "./referralStorage";

export { createFlyerInstanceId, normalizeFlyerInstanceId };

export const HELPER_FLYER_CUTOFF_DATE = "2026-03-15";
export const HELPER_FLYER_CUTOFF_LABEL = "Sunday, March 15";
export const HELPER_FLYER_CHALLENGE_ENABLED = true;
export const HELPER_FLYER_CHALLENGE_START_DATE =
  process.env.REACT_APP_FLYER_CHALLENGE_V2_START_DATE_KEY || "2026-08-13";
export const SENIOR_FLYER_QR_MEDIUM = QR_FLYER_MEDIUM;
export const SENIOR_FLYER_QR_CAMPAIGN = QR_FLYER_CAMPAIGN;
export const SENIOR_FLYER_QR_VERSION = QR_FLYER_VERSION;
export const SENIOR_FLYER_QR_BASE_URL = "https://ourlinkedlives.com/";
export const HELPER_MEMBER_REFERRAL_REWARD_MODES = Object.freeze({
  FIXED_PAID_MEMBER: "fixed_paid_member",
  VARIABLE_MEMBERSHIP_PERCENT: "variable_membership_percent",
});
// Keep this in sync with docker/referral-handler/engine/rewardRules.js.
export const HELPER_MEMBER_REFERRAL_REWARD_MODE =
  HELPER_MEMBER_REFERRAL_REWARD_MODES.FIXED_PAID_MEMBER;
export const HELPER_MEMBER_REFERRAL_FIXED_REWARD_CENTS = 2000;
export const HELPER_MEMBER_REFERRAL_VARIABLE_PERCENT = 0.5;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const getPrivateCollectionByType = (userType) => {
  if (userType === "member") return "membersPrivate";
  if (userType === "helper") return "employeesPrivate";
  return null;
};

export const EMPTY_REFERRAL_STATS = {
  totalApplied: 0,
  totalMemberConversions: 0,
  totalHelperActivated: 0,
  totalMemberRefunds: 0,
  totalRewardsIssued: 0,
  totalRewardsReversed: 0,
  totalRewardsCashCents: 0,
  totalRewardsDiscountCents: 0,
};

const REFERRAL_REWARD_REVERSAL_EVENT_TYPES = new Set([
  "reward_reversed",
  "reward_partially_reversed",
]);

const REFERRAL_REWARD_EVENT_TYPES = new Set([
  "reward_issued",
  ...REFERRAL_REWARD_REVERSAL_EVENT_TYPES,
]);

export const isReferralRewardReversalEventType = (eventType = "") =>
  REFERRAL_REWARD_REVERSAL_EVENT_TYPES.has(String(eventType || "").trim());

export const isReferralRewardEventType = (eventType = "") =>
  REFERRAL_REWARD_EVENT_TYPES.has(String(eventType || "").trim());

export const getReferralRewardCashCents = (event = {}) => {
  const amountCents = Number(event?.data?.rewardAmountCents || 0);
  if (Number.isFinite(amountCents) && amountCents !== 0) return amountCents;
  return Math.round(Number(event?.data?.rewardAmount || 0) * 100);
};

export const getReferralRewardCashDeltaCents = (event = {}) => {
  const kind = String(event?.data?.rewardKind || "");
  if (kind !== "cash") return 0;

  const amountCents = getReferralRewardCashCents(event);
  if (String(event?.eventType || "") === "reward_issued") return amountCents;
  if (isReferralRewardReversalEventType(event?.eventType || "")) {
    return -amountCents;
  }
  return 0;
};

export const getReferralRewardDiscountDeltaCents = (event = {}) => {
  const kind = String(event?.data?.rewardKind || "");
  if (kind !== "visit_discount") return 0;

  const amountCents = getReferralRewardCashCents(event);
  if (String(event?.eventType || "") === "reward_issued") return amountCents;
  if (isReferralRewardReversalEventType(event?.eventType || "")) {
    return -amountCents;
  }
  return 0;
};

export const normalizeReferralCode = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normalizeCents = (value = 0) => {
  const cents = Number(value || 0);
  if (!Number.isFinite(cents)) return 0;
  return Math.max(0, Math.round(cents));
};

const roundCentsToNearestFiveDollars = (cents = 0) =>
  Math.round(normalizeCents(cents) / 500) * 500;

const normalizeFlyerCoordinate = (value, maxAbs) => {
  const number = Number(value);
  if (!Number.isFinite(number) || Math.abs(number) > maxAbs) return "";
  return (Math.round(number * 1000) / 1000).toFixed(3);
};

const flyerCoordinateNumber = (value, maxAbs) => {
  const number = Number(value);
  if (!Number.isFinite(number) || Math.abs(number) > maxAbs) return null;
  return number;
};

const firstCoordinateInput = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

export const isUsableFlyerCoordinatePair = (latitude = "", longitude = "") => {
  const latNumber = flyerCoordinateNumber(latitude, 90);
  const lngNumber = flyerCoordinateNumber(longitude, 180);
  if (latNumber === null || lngNumber === null) return false;
  return !(Math.abs(latNumber) < 0.0005 && Math.abs(lngNumber) < 0.0005);
};

const normalizeFlyerVersion = (value = SENIOR_FLYER_QR_VERSION) =>
  String(value || SENIOR_FLYER_QR_VERSION)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 40) || SENIOR_FLYER_QR_VERSION;

export const getHelperMemberReferralRewardCents = ({
  mode = HELPER_MEMBER_REFERRAL_REWARD_MODE,
  membershipAmountCents = 0,
  fixedRewardCents = HELPER_MEMBER_REFERRAL_FIXED_REWARD_CENTS,
  percent = HELPER_MEMBER_REFERRAL_VARIABLE_PERCENT,
} = {}) => {
  if (mode === HELPER_MEMBER_REFERRAL_REWARD_MODES.VARIABLE_MEMBERSHIP_PERCENT) {
    const rawRewardCents = normalizeCents(membershipAmountCents) * Number(percent || 0);
    return roundCentsToNearestFiveDollars(rawRewardCents);
  }

  return normalizeCents(fixedRewardCents);
};

export const getReferralBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "https://ourlinkedlives.com";
};

export const buildMemberReferralLink = (code = "") => {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return "";
  return `${getReferralBaseUrl()}/join?referral=${normalized}`;
};

export const buildSeniorFlyerMemberReferralLink = (
  code = "",
  {
    flyerVersion = SENIOR_FLYER_QR_VERSION,
    flyerInstanceId = "",
  } = {},
) => {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return "";

  const params = new URLSearchParams({
    r: normalized,
    v: normalizeFlyerVersion(flyerVersion),
  });
  const normalizedFlyerInstanceId = normalizeFlyerInstanceId(flyerInstanceId);
  if (normalizedFlyerInstanceId) {
    params.set("f", normalizedFlyerInstanceId);
  }
  return `${SENIOR_FLYER_QR_BASE_URL}?${params.toString()}`;
};

export const buildHelperReferralLink = (code = "") => {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return "";
  return `${getReferralBaseUrl()}/hiring?referral=${normalized}`;
};

export const getSeniorFlyerReferralCode = (search = "") => {
  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(String(search || "").replace(/^\?/, ""));

  return normalizeReferralCode(
    params.get("code") || params.get("referral") || params.get("r") || "",
  );
};

export const buildSeniorFlyerQrUrl = (
  search = "",
  fallbackUrl = "",
  attribution = {},
) => {
  const attributedUrl = buildSeniorFlyerMemberReferralLink(
    getSeniorFlyerReferralCode(search),
    attribution,
  );
  if (attributedUrl) return attributedUrl;
  if (!fallbackUrl) return "";

  try {
    const url = new URL(fallbackUrl, getReferralBaseUrl());
    url.searchParams.set(
      "v",
      normalizeFlyerVersion(attribution.flyerVersion),
    );
    const normalizedFlyerInstanceId = normalizeFlyerInstanceId(
      attribution.flyerInstanceId,
    );
    if (normalizedFlyerInstanceId) {
      url.searchParams.set("f", normalizedFlyerInstanceId);
    }
    return url.href;
  } catch (error) {
    return fallbackUrl;
  }
};

export const buildSeniorFlyerLink = ({
  code = "",
  city = "",
  state = "",
  latitude = "",
  longitude = "",
  lat = "",
  lng = "",
} = {}) => {
  const normalized = normalizeReferralCode(code);
  const params = new URLSearchParams();
  const rawLat = firstCoordinateInput(latitude, lat);
  const rawLng = firstCoordinateInput(longitude, lng);
  const normalizedLat = isUsableFlyerCoordinatePair(rawLat, rawLng)
    ? normalizeFlyerCoordinate(rawLat, 90)
    : "";
  const normalizedLng = normalizedLat ? normalizeFlyerCoordinate(rawLng, 180) : "";

  if (normalized) params.set("code", normalized);
  if (normalizedLat && normalizedLng) {
    params.set("lat", normalizedLat);
    params.set("lng", normalizedLng);
  }
  if (String(city || "").trim()) params.set("city", String(city || "").trim());
  if (String(state || "").trim()) params.set("state", String(state || "").trim());

  const query = params.toString();
  return `${getReferralBaseUrl()}/flyers/seniors${query ? `?${query}` : ""}`;
};

export const isFlyerCashProgramActive = (now = DateTime.now()) => {
  const zonedNow = now.setZone("America/Los_Angeles");

  if (HELPER_FLYER_CHALLENGE_ENABLED) {
    const start = DateTime.fromISO(HELPER_FLYER_CHALLENGE_START_DATE, {
      zone: "America/Los_Angeles",
    }).startOf("day");
    return zonedNow >= start;
  }

  const cutoff = DateTime.fromISO(HELPER_FLYER_CUTOFF_DATE, {
    zone: "America/Los_Angeles",
  }).startOf("day");

  return zonedNow < cutoff;
};

export const getReferralLeaderboardBucketKey = (
  windowId,
  now = DateTime.now().setZone("America/Los_Angeles"),
) => {
  if (windowId === "week") return now.toFormat("kkkk-'W'WW");
  if (windowId === "month") return now.toFormat("yyyy-LL");
  if (windowId === "year") return now.toFormat("yyyy");
  return "all";
};

export const getReferralLeaderboardDocId = (windowId, now = DateTime.now()) => {
  const bucketKey = getReferralLeaderboardBucketKey(windowId, now);
  return `helpers_${windowId}_${bucketKey}`;
};

const normalizeStartAt = (startAt = null) => {
  if (!startAt) return null;
  if (typeof startAt?.toMillis === "function") return startAt;
  if (startAt instanceof Date) return Timestamp.fromDate(startAt);

  const millis = Number(startAt);
  if (!Number.isFinite(millis)) return null;
  return Timestamp.fromMillis(millis);
};

const buildReferralFeedQuery = ({
  userId,
  userType,
  eventType = "",
  rewardKind = "",
  startAt = null,
} = {}) => {
  const normalizedUserId = String(userId || "").trim();
  const privateCollection = getPrivateCollectionByType(userType);
  if (!normalizedUserId || !privateCollection) return null;

  const constraints = [where("stream", "==", "referrals")];
  if (String(eventType || "").trim()) {
    constraints.push(where("eventType", "==", String(eventType || "").trim()));
  }
  if (String(rewardKind || "").trim()) {
    constraints.push(where("data.rewardKind", "==", String(rewardKind || "").trim()));
  }

  const normalizedStartAt = normalizeStartAt(startAt);
  if (normalizedStartAt) {
    constraints.push(where("createdAt", ">=", normalizedStartAt));
  }

  return query(
    collection(db, privateCollection, normalizedUserId, "feed"),
    ...constraints,
  );
};

export const getAggregateWindowStart = (days) => {
  const normalizedDays = Number(days);
  if (!Number.isFinite(normalizedDays) || normalizedDays <= 0) return null;
  return Timestamp.fromMillis(Date.now() - normalizedDays * DAY_IN_MS);
};

export const getReferralAggregateStats = async ({
  userId,
  userType,
  startAt = null,
} = {}) => {
  const appliedQuery = buildReferralFeedQuery({
    userId,
    userType,
    eventType: "referral_applied",
    startAt,
  });

  if (!appliedQuery) {
    return { ...EMPTY_REFERRAL_STATS };
  }

  const [
    appliedSnapshot,
    memberSnapshot,
    helperSnapshot,
    refundedSnapshot,
    cashRewardsIssuedSnapshot,
    cashRewardsReversedSnapshot,
    cashPartialRewardsReversedSnapshot,
    discountRewardsIssuedSnapshot,
    discountRewardsReversedSnapshot,
    discountPartialRewardsReversedSnapshot,
  ] = await Promise.all([
    getAggregateFromServer(appliedQuery, { total: count() }),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "referee_became_member",
        startAt,
      }),
      { total: count() },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "referee_became_helper",
        startAt,
      }),
      { total: count() },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "membership_refunded",
        startAt,
      }),
      { total: count() },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "reward_issued",
        rewardKind: "cash",
        startAt,
      }),
      {
        total: count(),
        cashTotal: sum("data.rewardAmountCents"),
      },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "reward_reversed",
        rewardKind: "cash",
        startAt,
      }),
      {
        total: count(),
        cashTotal: sum("data.rewardAmountCents"),
      },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "reward_partially_reversed",
        rewardKind: "cash",
        startAt,
      }),
      {
        total: count(),
        cashTotal: sum("data.rewardAmountCents"),
      },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "reward_issued",
        rewardKind: "visit_discount",
        startAt,
      }),
      {
        total: count(),
        discountTotal: sum("data.rewardAmountCents"),
      },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "reward_reversed",
        rewardKind: "visit_discount",
        startAt,
      }),
      {
        total: count(),
        discountTotal: sum("data.rewardAmountCents"),
      },
    ),
    getAggregateFromServer(
      buildReferralFeedQuery({
        userId,
        userType,
        eventType: "reward_partially_reversed",
        rewardKind: "visit_discount",
        startAt,
      }),
      {
        total: count(),
        discountTotal: sum("data.rewardAmountCents"),
      },
    ),
  ]);

  const cashRewardsIssuedData = cashRewardsIssuedSnapshot.data() || {};
  const cashRewardsReversedData = cashRewardsReversedSnapshot.data() || {};
  const cashPartialRewardsReversedData =
    cashPartialRewardsReversedSnapshot.data() || {};
  const discountRewardsIssuedData = discountRewardsIssuedSnapshot.data() || {};
  const discountRewardsReversedData =
    discountRewardsReversedSnapshot.data() || {};
  const discountPartialRewardsReversedData =
    discountPartialRewardsReversedSnapshot.data() || {};

  return {
    totalApplied: Number(appliedSnapshot.data()?.total || 0),
    totalMemberConversions: Number(memberSnapshot.data()?.total || 0),
    totalHelperActivated: Number(helperSnapshot.data()?.total || 0),
    totalMemberRefunds: Number(refundedSnapshot.data()?.total || 0),
    totalRewardsIssued:
      Number(cashRewardsIssuedData.total || 0) +
      Number(discountRewardsIssuedData.total || 0),
    totalRewardsReversed:
      Number(cashRewardsReversedData.total || 0) +
      Number(cashPartialRewardsReversedData.total || 0) +
      Number(discountRewardsReversedData.total || 0) +
      Number(discountPartialRewardsReversedData.total || 0),
    totalRewardsCashCents:
      Number(cashRewardsIssuedData.cashTotal || 0) -
      Number(cashRewardsReversedData.cashTotal || 0) -
      Number(cashPartialRewardsReversedData.cashTotal || 0),
    totalRewardsDiscountCents:
      Number(discountRewardsIssuedData.discountTotal || 0) -
      Number(discountRewardsReversedData.discountTotal || 0) -
      Number(discountPartialRewardsReversedData.discountTotal || 0),
  };
};

export const getHelperReferralAggregateStats = async ({
  helperId,
  startAt = null,
} = {}) =>
  getReferralAggregateStats({
    userId: helperId,
    userType: "helper",
    startAt,
  });

export const buildReferralStatsSummary = ({
  ownerId,
  ownerType,
  stats = {},
} = {}) => {
  const normalizedOwnerType = String(ownerType || "").trim().toLowerCase();
  const sectionPrefixes =
    normalizedOwnerType === "helper"
      ? { member: "h2m", helper: "h2h" }
      : { member: "m2m", helper: "m2h" };

  const pickSectionSummary = (prefix = "") => ({
    appliedCount: Number(stats?.[`${prefix}AppliedCount`] || 0),
    joinedCount: Number(stats?.[`${prefix}MemberConversionCount`] || 0),
    activatedCount: Number(stats?.[`${prefix}HelperActivationCount`] || 0),
    pendingCount: Number(stats?.[`${prefix}PendingCount`] || 0),
    activeCount: Number(stats?.[`${prefix}BenefitingCount`] || 0),
    completedCount: Number(stats?.[`${prefix}ClosedCount`] || 0),
    refundedCount: Number(stats?.[`${prefix}MemberRefundCount`] || 0),
    totalRewardsHours: Number(stats?.[`${prefix}RewardHours`] || 0),
    totalRewardsCash: Number(stats?.[`${prefix}RewardCash`] || 0),
    totalRewardsCashCents: Number(stats?.[`${prefix}RewardCashCents`] || 0),
    totalRewardsDiscountCents: Number(
      stats?.[`${prefix}RewardDiscountCents`] || 0,
    ),
    totalEarnedCents: Number(stats?.[`${prefix}RewardCashCents`] || 0),
    totalRewardsIssued: Number(stats?.[`${prefix}RewardIssuedCount`] || 0),
    totalRewardsReversed: Number(stats?.[`${prefix}RewardReversedCount`] || 0),
  });

  return {
    ownerId: String(ownerId || ""),
    ownerType: normalizedOwnerType,
    summary: {
      totalApplied: Number(stats?.totalApplied || 0),
      totalEarnedCents: Number(stats?.totalRewardsCashCents || 0),
      totalRewardsHours: Number(stats?.totalRewardsHours || 0),
      totalRewardsCash: Number(stats?.totalRewardsCash || 0),
      totalRewardsCashCents: Number(stats?.totalRewardsCashCents || 0),
      totalRewardsDiscountCents: Number(
        stats?.totalRewardsDiscountCents || 0,
      ),
      totalRewardsIssued: Number(stats?.totalRewardsIssued || 0),
      totalRewardsReversed: Number(stats?.totalRewardsReversed || 0),
      totalMembersJoined: Number(stats?.totalMemberConversions || 0),
      totalHelpersActivated: Number(stats?.totalHelperActivated || 0),
      totalMemberRefunds: Number(stats?.totalMemberRefunds || 0),
    },
    memberReferral: pickSectionSummary(sectionPrefixes.member),
    helperReferral: pickSectionSummary(sectionPrefixes.helper),
    raw: stats || {},
  };
};

export const summaryHasBucketCounts = (
  referralStatsSummary = null,
  ownerType = "",
) => {
  const normalizedOwnerType = String(ownerType || "").trim().toLowerCase();
  const raw = referralStatsSummary?.raw || {};
  const prefixes =
    normalizedOwnerType === "helper"
      ? ["h2m", "h2h"]
      : normalizedOwnerType === "member"
        ? ["m2m", "m2h"]
        : [];

  return prefixes.every(
    (prefix) =>
      Object.prototype.hasOwnProperty.call(raw, `${prefix}PendingCount`) &&
      Object.prototype.hasOwnProperty.call(raw, `${prefix}BenefitingCount`) &&
      Object.prototype.hasOwnProperty.call(raw, `${prefix}ClosedCount`),
  );
};

export const subscribeReferralStatsSummary = ({
  ownerId,
  ownerType,
  onData = () => {},
  onError = () => {},
} = {}) => {
  const normalizedOwnerId = String(ownerId || "").trim();
  const privateCollection = getPrivateCollectionByType(ownerType);

  if (!normalizedOwnerId || !privateCollection) {
    onData(
      buildReferralStatsSummary({
        ownerId: normalizedOwnerId,
        ownerType,
        stats: {},
      }),
    );
    return () => {};
  }

  return onSnapshot(
    doc(db, privateCollection, normalizedOwnerId, "referralStats", "summary"),
    (snapshot) => {
      onData(
        buildReferralStatsSummary({
          ownerId: normalizedOwnerId,
          ownerType,
          stats: snapshot.exists() ? snapshot.data() || {} : {},
        }),
      );
    },
    onError,
  );
};

export const ensureReferralCodeForOwner = async (ownerType) => {
  const response = await callCloudRunFunction(
    "referral-handler",
    "ensureReferralCode",
    { ownerType },
  );

  return {
    ...response,
    code: normalizeReferralCode(response?.code || ""),
  };
};

export const syncReferralBucketCounts = async ({
  ownerId,
  ownerType,
} = {}) =>
  callCloudRunFunction("referral-handler", "syncReferralBucketCounts", {
    ownerId,
    ownerType,
  });

export const getReferralItemsPage = async ({
  ownerId,
  ownerType,
  referralType,
  bucket,
  pageSize,
  cursor,
} = {}) =>
  callCloudRunFunction("referral-handler", "getReferralItemsPage", {
    ownerId,
    ownerType,
    referralType,
    bucket,
    pageSize,
    cursor,
  });
